import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuItem from '@mui/material/MenuItem';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import Select from '@mui/material/Select';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  PA_BULK_LIMIT,
  allocateProductAccess,
  getProductAccessTerms,
  getProductAccesses,
  getWorkspacesForCA,
} from '@account/api';
import {formatErrorMsg, getError} from '@account/utils/errors';
import {plainDateStamp} from '@account/utils/dates';
import {renderQuota, translateQuotaUnits} from '@account/utils/quota';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

// Scope policies for which of each product's terms receive an allocation.
// Resolved PER PRODUCT (each product owns its own independent terms), so a scope
// is a policy, not a shared calendar object.
export const SCOPE_ACTIVE = 'active';
export const SCOPE_ACTIVE_AND_FUTURE = 'active_future';

// Label strings must match the single-product dialog's wording.
export const SCOPE_ACTIVE_LABEL = 'Active term only';
export const SCOPE_ACTIVE_AND_FUTURE_LABEL = 'Active + all future terms';

// Classify a product access the same way AllocationDialog does.
// - unlimited: unlimited_quota flag set
// - access-only: no numeric quota total (null/undefined) and not unlimited
// - quota: finite numeric quota
const classifyPA = pa => {
  const isUnlimited = !!pa?.unlimited_quota;
  const isAccessOnly =
    (pa?.quota_total === null || pa?.quota_total === undefined) && !isUnlimited;
  const isQuota = !isUnlimited && !isAccessOnly;
  const remaining = isQuota
    ? (pa?.quota_total ?? 0) - (pa?.quota_used ?? 0)
    : null;
  return {isUnlimited, isAccessOnly, isQuota, remaining};
};

// Return the terms in scope for a product, honest per-term "remaining", ordered
// by start_date. NEVER includes expired/inactive/deactivated terms.
//
// active = activation_state 'active'; future = activation_state 'pending'.
//
// Per-term remaining approximation (documented on purpose): fetching
// getTermBalance for every product x term is an N x M fan-out that adds a large
// failure surface to what is only a preview. Instead:
//   - ACTIVE term(s): keep today's honest behavior - the PA-level remaining
//     (quota_total - quota_used) split across active terms proportionally by
//     each term's quota_limit (last term absorbs the rounding remainder so the
//     shares sum exactly to the PA remaining).
//   - FUTURE/pending term(s): nothing has been consumed yet, so the term's
//     quota_limit is its remaining.
// This stays honest without the balance fan-out. Swap in getTermBalance here if
// per-term consumption ever diverges from this within a scope.
const sortByStart = rows =>
  [...rows].sort(
    (a, b) => new Date(a.term.start_date) - new Date(b.term.start_date)
  );

export const resolveTermsInScope = (terms, pa, scope) => {
  const active = (terms ?? []).filter(t => t.activation_state === 'active');
  const future = (terms ?? []).filter(t => t.activation_state === 'pending');

  const {remaining: paRemaining} = classifyPA(pa);
  const activeQuotaTotal = active.reduce((s, t) => s + (t.quota_limit ?? 0), 0);

  // Split PA remaining across active terms proportionally by quota_limit, last
  // term absorbing the remainder so the split sums exactly.
  const orderedActive = [...active].sort(
    (a, b) => new Date(a.start_date) - new Date(b.start_date)
  );
  let allocated = 0;
  const activeRows = orderedActive.map((term, i) => {
    const share =
      i === orderedActive.length - 1
        ? (paRemaining ?? 0) - allocated
        : activeQuotaTotal > 0
          ? Math.floor(
              ((paRemaining ?? 0) * (term.quota_limit ?? 0)) / activeQuotaTotal
            )
          : 0;
    allocated += share;
    return {term, remaining: share, state: 'active'};
  });

  if (scope === SCOPE_ACTIVE) {
    return sortByStart(activeRows);
  }

  const futureRows = future.map(term => ({
    term,
    remaining: term.quota_limit ?? 0,
    state: 'pending',
  }));
  return sortByStart([...activeRows, ...futureRows]);
};

// Compute the per-term child rows and the bottom-up parent aggregate for one
// product, given resolved in-scope terms and the percentage. Percentage applies
// to EACH term's remaining independently, rounded per term (each term is its own
// backend write), then summed. The parent total is the sum of the rounded child
// values so parent always equals the sum of visible children.
export const buildTermChildren = (termRows, pct) =>
  (termRows ?? []).map(row => {
    const amount = Math.floor((row.remaining ?? 0) * (pct / 100));
    const skipped = (row.remaining ?? 0) <= 0 || amount <= 0;
    return {
      term: row.term,
      state: row.state,
      remaining: row.remaining,
      amount,
      skipped,
      skipReason: skipped
        ? (row.remaining ?? 0) <= 0
          ? 'Nothing remaining'
          : 'Rounds to 0'
        : null,
    };
  });

// Build the per-PA plan for a given percentage and scope. Access-only and
// unlimited PAs join the transfer as access ('provided') - but only if that
// product isn't already in the destination workspace. `existingEdpIds` is the
// set of edp_ids already present in the selected workspace. Quota PAs resolve
// their in-scope terms from `termsByPaId` (keyed by pa.id), then allocate
// floor(remaining * pct / 100) per term in the product's own units.
//
// `termsByPaId` maps pa.id -> {terms, isLoading, isError}. A quota PA still
// loading its terms is marked loading (excluded from the write, preview shows a
// spinner). A quota PA whose fetch errored, or that has no term in the chosen
// scope, is skipped and NEVER fails the batch.
export const buildBulkPlan = (
  productAccesses,
  pct,
  existingEdpIds = new Set(),
  scope = SCOPE_ACTIVE,
  termsByPaId = {}
) => {
  return (productAccesses ?? []).map(pa => {
    const {isUnlimited, isQuota} = classifyPA(pa);

    if (isQuota) {
      const termState = termsByPaId[pa.id] ?? {};
      if (termState.isLoading) {
        return {
          pa,
          type: 'quota',
          units: pa?.quota_units,
          loading: true,
          children: [],
          totalAmount: 0,
          totalRemaining: 0,
          skipped: true,
          skipReason: 'Loading terms',
        };
      }
      if (termState.isError) {
        return {
          pa,
          type: 'quota',
          units: pa?.quota_units,
          children: [],
          totalAmount: 0,
          totalRemaining: 0,
          skipped: true,
          skipReason: 'Failed to load terms',
        };
      }

      const termRows = resolveTermsInScope(termState.terms, pa, scope);
      if (termRows.length === 0) {
        return {
          pa,
          type: 'quota',
          units: pa?.quota_units,
          children: [],
          totalAmount: 0,
          totalRemaining: 0,
          skipped: true,
          skipReason: 'No term in scope',
        };
      }

      const children = buildTermChildren(termRows, pct);
      // Bottom-up: parent totals are the sum of the (rounded) child values so
      // the parent always equals the sum of the visible children.
      const totalAmount = children.reduce(
        (s, c) => s + (c.skipped ? 0 : c.amount),
        0
      );
      const totalRemaining = children.reduce(
        (s, c) => s + (c.remaining ?? 0),
        0
      );
      const anyWritable = children.some(c => !c.skipped);
      return {
        pa,
        type: 'quota',
        units: pa?.quota_units,
        children,
        totalAmount,
        totalRemaining,
        skipped: !anyWritable,
        skipReason: anyWritable ? null : 'Nothing remaining',
      };
    }

    // access-only or unlimited: join as access, unless already in the workspace
    const alreadyExists = existingEdpIds.has(pa?.edp_id);
    return {
      pa,
      type: isUnlimited ? 'unlimited' : 'access',
      units: pa?.quota_units,
      children: [],
      totalAmount: null,
      totalRemaining: isUnlimited ? Infinity : null,
      skipped: alreadyExists,
      skipReason: alreadyExists ? 'Already in workspace' : null,
    };
  });
};

// Split a requested quota amount across a product's currently-active term(s).
// term_start_date/term_end_date on the product access span every non-expired
// term (including future-scheduled ones), so a revoke/allocate request built
// from those dates can span terms it shouldn't touch. Scope each request to
// one actual term's own dates instead. Most products have exactly one active
// term (the split is a no-op); a product with stacked concurrent terms gets
// its amount split proportionally by each term's quota_limit, remainder
// going to the last term (ordered by start_date) so shares sum exactly.
//
// Retained for callers/tests that scope a single amount to the active term(s).
// The bulk submit path now builds per-term segments directly from the plan.
export const splitAmountAcrossActiveTerms = (terms, totalAmount) => {
  const activeTerms = (terms ?? []).filter(
    t => t.activation_state === 'active'
  );
  if (activeTerms.length === 0) return [];

  const ordered = [...activeTerms].sort(
    (a, b) => new Date(a.start_date) - new Date(b.start_date)
  );
  const totalActiveQuota = ordered.reduce(
    (s, t) => s + (t.quota_limit ?? 0),
    0
  );
  let allocated = 0;
  return ordered
    .map((term, i) => {
      const share =
        i === ordered.length - 1
          ? totalAmount - allocated
          : totalActiveQuota > 0
            ? Math.floor((totalAmount * term.quota_limit) / totalActiveQuota)
            : 0;
      allocated += share;
      return {term, amount: share};
    })
    .filter(({amount}) => amount > 0);
};

// One in-scope term rendered as an expandable child row.
const termLabel = child => {
  const start = plainDateStamp(child.term.start_date);
  const end = plainDateStamp(child.term.end_date);
  return `${start} – ${end} (${child.state === 'active' ? 'active' : 'future'})`;
};

const BulkAllocationDialog = ({accountId, open, onClose, productAccesses}) => {
  const queryClient = useQueryClient();
  const [destinationWorkspaceId, setDestinationWorkspaceId] = useState('');
  const [percentInput, setPercentInput] = useState('100');
  const [scope, setScope] = useState(SCOPE_ACTIVE);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState(null);
  // pa.id -> bool: rows the operator has opted OUT of (unchecked).
  const [excludedPaIds, setExcludedPaIds] = useState({});
  // pa.id -> bool: which parent rows are expanded to show per-term children.
  const [expandedPaIds, setExpandedPaIds] = useState({});
  // After a run, the set of failed pa.ids so "Retry failed only" can re-run.
  const [retryOnly, setRetryOnly] = useState(null);

  const parsedPct = percentInput === '' ? null : Number(percentInput);
  const pctInvalid =
    parsedPct === null ||
    isNaN(parsedPct) ||
    !Number.isInteger(parsedPct) ||
    parsedPct < 1 ||
    parsedPct > 100;

  const orgsQuery = useQuery({
    queryKey: ['workspaces-for-ca', accountId],
    queryFn: () => getWorkspacesForCA(accountId),
    enabled: !!accountId,
  });
  const workspaceTruncated = orgsQuery.data?.truncated ?? false;
  const workspaceOptions = useMemo(
    () => orgsQuery.data?.workspaces ?? [],
    [orgsQuery.data]
  );

  // Only one workspace? Pre-select it so the operator doesn't have to. Guard on
  // `enabled` - a lone disabled workspace can't receive an allocation.
  useEffect(() => {
    if (destinationWorkspaceId) return;
    const enabled = workspaceOptions.filter(ws => ws.enabled);
    if (enabled.length === 1)
      setDestinationWorkspaceId(enabled[0].workspace_id);
  }, [workspaceOptions, destinationWorkspaceId]);

  // Product accesses already allocated across this CA's workspaces. Used to skip
  // access-only/unlimited products that already exist in the destination. Shares
  // the parent page's cache key so a fetch is often already warm.
  const workspacePasQuery = useQuery({
    queryKey: ['caWorkspacePas', accountId],
    queryFn: () =>
      getProductAccesses({
        customerAccountId: accountId,
        limit: PA_BULK_LIMIT,
      }),
    enabled: !!accountId && open,
  });
  const existingEdpIds = useMemo(() => {
    const ids = new Set();
    if (!destinationWorkspaceId) return ids;
    for (const pa of workspacePasQuery.data?.results ?? []) {
      if (pa.workspace_id === destinationWorkspaceId && pa.edp_id) {
        ids.add(pa.edp_id);
      }
    }
    return ids;
  }, [workspacePasQuery.data, destinationWorkspaceId]);

  // Only quota PAs need their terms; access-only/unlimited never allocate quota.
  const quotaPas = useMemo(
    () => (productAccesses ?? []).filter(pa => classifyPA(pa).isQuota),
    [productAccesses]
  );

  // Fetch each quota product's terms once, when the dialog is open. Per-term
  // remaining is derived from these (see resolveTermsInScope).
  const termQueries = useQueries({
    queries: quotaPas.map(pa => ({
      queryKey: ['product-access-terms', pa.id],
      queryFn: () => getProductAccessTerms(pa.id),
      enabled: !!pa.id && open,
    })),
  });

  const termsByPaId = useMemo(() => {
    const map = {};
    quotaPas.forEach((pa, i) => {
      const q = termQueries[i];
      const raw = q?.data;
      const terms = Array.isArray(raw) ? raw : (raw?.results ?? []);
      map[pa.id] = {
        terms,
        isLoading: q?.isLoading ?? false,
        isError: q?.isError ?? false,
      };
    });
    return map;
    // termQueries is a fresh array each render; depend on its status/data slices.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotaPas, termQueries.map(q => `${q.status}:${!!q.data}`).join('|')]);

  const termsLoading = termQueries.some(q => q.isLoading);

  const plan = useMemo(
    () =>
      buildBulkPlan(
        productAccesses,
        pctInvalid ? 0 : parsedPct,
        existingEdpIds,
        scope,
        termsByPaId
      ),
    [productAccesses, parsedPct, pctInvalid, existingEdpIds, scope, termsByPaId]
  );

  // A row participates if the plan didn't skip it AND the operator didn't opt
  // out of it.
  const isExcluded = row => !!excludedPaIds[row.pa.id];
  const participating = plan.filter(row => !row.skipped && !isExcluded(row));
  const skipped = plan.filter(row => row.skipped);
  const optedOut = plan.filter(row => !row.skipped && isExcluded(row));

  // Blast radius: products and terms actually being written.
  const productCount = participating.length;
  const termCount = participating.reduce((s, row) => {
    if (row.type !== 'quota') return s + 1;
    return s + row.children.filter(c => !c.skipped).length;
  }, 0);
  const skippedCount = skipped.length + optedOut.length;

  const mutation = useMutation({
    mutationFn: ({productAccessId, data}) =>
      allocateProductAccess(productAccessId, data),
  });

  const invalidate = () => {
    queryClient.invalidateQueries(['productAccesses']);
    queryClient.invalidateQueries(['entitlements']);
    queryClient.invalidateQueries(['caWorkspacePas', accountId]);
  };

  // Build the amounts segments for one plan row. One segment per in-scope,
  // non-skipped term (or a single 'provided' segment for access/unlimited).
  const buildAmounts = row => {
    if (row.type !== 'quota') {
      return [
        {
          start_date: row.pa?.term_start_date,
          end_date: row.pa?.term_end_date,
          quota_amount: 'provided',
        },
      ];
    }
    return row.children
      .filter(c => !c.skipped)
      .map(c => ({
        start_date: c.term.start_date,
        end_date: c.term.end_date,
        quota_amount: c.amount,
        quota_units: row.units,
      }));
  };

  const runAllocation = async rows => {
    setRunning(true);
    setSummary(null);
    const succeeded = [];
    const failed = [];

    for (const row of rows) {
      const {pa} = row;
      const amounts = buildAmounts(row);
      if (amounts.length === 0) continue;
      try {
        // Loop client-side: there is no bulk backend endpoint. One call per
        // product carries all its in-scope term segments. Do not abort the
        // whole batch when a single PA fails.
        // eslint-disable-next-line no-await-in-loop
        await mutation.mutateAsync({
          productAccessId: pa.id,
          data: {
            destination_customer_account_id: accountId,
            destination_workspace_id: destinationWorkspaceId,
            amounts,
          },
        });
        succeeded.push({id: pa.id, name: pa.title || pa.edp_id});
      } catch (err) {
        failed.push({
          id: pa.id,
          name: pa.title || pa.edp_id,
          // Surface the backend's field-level `detail` (e.g. the specific
          // dependency/exclusion constraint) rather than the generic wrapper.
          message: formatErrorMsg(getError(err)) || err?.message || 'Failed',
        });
      }
    }

    invalidate();
    setRetryOnly(new Set(failed.map(f => f.id)));
    setSummary({
      succeeded,
      failed,
      skipped: skipped.map(row => ({
        id: row.pa.id,
        name: row.pa.title || row.pa.edp_id,
      })),
    });
    setRunning(false);
  };

  const handleSubmit = () => runAllocation(participating);

  const handleRetryFailed = () => {
    if (!retryOnly || retryOnly.size === 0) return;
    const rows = plan.filter(row => retryOnly.has(row.pa.id));
    runAllocation(rows);
  };

  const handleClose = () => {
    setDestinationWorkspaceId('');
    setPercentInput('100');
    setScope(SCOPE_ACTIVE);
    setExcludedPaIds({});
    setExpandedPaIds({});
    setRetryOnly(null);
    setSummary(null);
    mutation.reset();
    onClose();
  };

  const toggleExcluded = id =>
    setExcludedPaIds(prev => ({...prev, [id]: !prev[id]}));
  const toggleExpanded = id =>
    setExpandedPaIds(prev => ({...prev, [id]: !prev[id]}));

  const canSubmit =
    !!destinationWorkspaceId &&
    !pctInvalid &&
    participating.length > 0 &&
    !running &&
    !summary &&
    !termsLoading &&
    !workspacePasQuery.isInitialLoading;

  // Per-unit-type totals footer. NEVER sum across mixed units - group by units
  // and render each ("2,000,000 pu · 500,000 im").
  const unitTotals = useMemo(() => {
    const byUnit = {};
    for (const row of participating) {
      if (row.type !== 'quota') continue;
      byUnit[row.units] = (byUnit[row.units] ?? 0) + row.totalAmount;
    }
    return Object.entries(byUnit).filter(([, amount]) => amount > 0);
  }, [participating]);

  const renderRowRemaining = row => {
    if (row.type === 'unlimited') return 'Unlimited';
    if (row.type === 'access') return '—';
    if (row.loading) return <CircularProgress size={14} />;
    return renderQuota(row.totalRemaining, row.units);
  };

  const renderRowAmount = row => {
    if (row.type !== 'quota') return 'Access';
    if (row.loading) return <CircularProgress size={14} />;
    return row.skipped ? '—' : renderQuota(row.totalAmount, row.units);
  };

  // A single-term active-only quota row shows its resolved term inline and
  // needs no expansion. Multi-term rows get a chevron.
  const writableChildCount = row =>
    row.type === 'quota' ? row.children.length : 0;
  const isExpandable = row => writableChildCount(row) > 1;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Bulk allocate to workspace
        <Typography variant="body2" color="text.secondary">
          Allocate a percentage of each product&apos;s remaining quota to one
          workspace, scoped by term.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{mt: 1}}>
          {summary && (
            <Alert severity={summary.failed.length > 0 ? 'warning' : 'success'}>
              {summary.succeeded.length} allocated
              {summary.failed.length > 0 && `, ${summary.failed.length} failed`}
              {summary.skipped.length > 0 &&
                `, ${summary.skipped.length} skipped`}
              .
              {summary.failed.length > 0 && (
                <>
                  <Box component="ul" sx={{mt: 1, mb: 0, pl: 3}}>
                    {summary.failed.map(f => (
                      <li key={f.id}>
                        {f.name}: {f.message}
                      </li>
                    ))}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{mt: 1}}
                    onClick={handleRetryFailed}
                    disabled={running}
                  >
                    Retry failed only
                  </Button>
                </>
              )}
            </Alert>
          )}

          {workspaceTruncated && (
            <Alert severity="warning">
              Showing first 100 workspaces. If the target workspace is missing,
              search the account by workspace ID.
            </Alert>
          )}

          <FormControl size="small" fullWidth required>
            <InputLabel
              id="bulk-workspace-label"
              shrink={!!destinationWorkspaceId}
            >
              Destination workspace
            </InputLabel>
            <Select
              labelId="bulk-workspace-label"
              label="Destination workspace"
              value={destinationWorkspaceId}
              onChange={e => setDestinationWorkspaceId(e.target.value)}
              disabled={orgsQuery.isInitialLoading || running || !!summary}
              displayEmpty
              renderValue={value => {
                if (value === '') return '';
                const ws = workspaceOptions.find(w => w.workspace_id === value);
                return ws?.name || value;
              }}
              endAdornment={
                orgsQuery.isLoading ? (
                  <CircularProgress size={16} sx={{mr: 3}} />
                ) : null
              }
            >
              {workspaceOptions.map(org => {
                const isUuidOnly = org.name === org.workspace_id;
                const displayName = isUuidOnly
                  ? org.workspace_id.slice(0, 8) + '…'
                  : org.name;
                return (
                  <MenuItem
                    key={org.workspace_id}
                    value={org.workspace_id}
                    disabled={!org.enabled}
                  >
                    <Box>
                      <Typography variant="body2">{displayName}</Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{fontFamily: 'monospace', display: 'block'}}
                      >
                        {org.workspace_id}
                        {!org.enabled && ' · disabled'}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
              {orgsQuery.isError && (
                <MenuItem disabled value="">
                  Failed to load workspaces
                </MenuItem>
              )}
              {!orgsQuery.isLoading &&
                !orgsQuery.isError &&
                workspaceOptions.length === 0 && (
                  <MenuItem disabled value="">
                    No workspaces found
                  </MenuItem>
                )}
            </Select>
          </FormControl>

          <FormControl disabled={running || !!summary}>
            <Typography variant="subtitle2" mb={0.5}>
              Term scope
            </Typography>
            <RadioGroup
              value={scope}
              onChange={e => {
                setScope(e.target.value);
                setSummary(null);
              }}
            >
              <FormControlLabel
                value={SCOPE_ACTIVE}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">
                      {SCOPE_ACTIVE_LABEL}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Allocates from each product&apos;s current active term
                      only.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value={SCOPE_ACTIVE_AND_FUTURE}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">
                      {SCOPE_ACTIVE_AND_FUTURE_LABEL}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Also allocates from every pending/future term of each
                      product.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          <TextField
            label="Allocate __% of remaining quota (applied per term)"
            type="number"
            value={percentInput}
            onChange={e => {
              setPercentInput(e.target.value);
              setSummary(null);
            }}
            inputProps={{min: 1, max: 100, step: 1}}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            size="small"
            fullWidth
            required
            disabled={running || !!summary}
            error={percentInput !== '' && pctInvalid}
            helperText={
              percentInput !== '' && pctInvalid
                ? 'Enter a whole number between 1 and 100'
                : null
            }
          />

          <Box>
            <Typography variant="subtitle2" mb={1}>
              Preview
            </Typography>

            {/* Blast-radius line: live count that updates on scope/selection. */}
            <Typography
              variant="body2"
              color="text.secondary"
              mb={1}
              data-testid="blast-radius"
            >
              {termsLoading
                ? 'Resolving terms…'
                : `Allocating to ${productCount} product${
                    productCount === 1 ? '' : 's'
                  } across ${termCount} term${termCount === 1 ? '' : 's'}${
                    skippedCount > 0 ? ` · ${skippedCount} skipped` : ''
                  }`}
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell />
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell align="right">
                    × {pctInvalid ? '—' : parsedPct}%
                  </TableCell>
                  <TableCell align="right">To allocate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plan.map(row => {
                  const expandable = isExpandable(row);
                  const expanded = !!expandedPaIds[row.pa.id];
                  const singleActiveChild =
                    row.type === 'quota' &&
                    writableChildCount(row) === 1 &&
                    row.children[0];
                  const disabledRow = row.skipped;
                  return (
                    <React.Fragment key={row.pa.id}>
                      <TableRow
                        sx={
                          disabledRow || isExcluded(row)
                            ? {opacity: 0.5}
                            : undefined
                        }
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={!row.skipped && !isExcluded(row)}
                            disabled={row.skipped || running || !!summary}
                            onChange={() => toggleExcluded(row.pa.id)}
                            inputProps={{
                              'aria-label': `Include ${
                                row.pa.title || row.pa.edp_id
                              }`,
                            }}
                          />
                        </TableCell>
                        <TableCell padding="checkbox">
                          {expandable && (
                            <IconButton
                              size="small"
                              onClick={() => toggleExpanded(row.pa.id)}
                              aria-label={
                                expanded ? 'Collapse terms' : 'Expand terms'
                              }
                            >
                              {expanded ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.pa.title || row.pa.edp_id}
                          {row.type === 'quota' &&
                            !row.loading &&
                            singleActiveChild && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{display: 'block'}}
                              >
                                {termLabel(singleActiveChild)}
                              </Typography>
                            )}
                          {expandable && !expanded && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{display: 'block'}}
                            >
                              {writableChildCount(row)} terms
                            </Typography>
                          )}
                          {row.skipped && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{display: 'block'}}
                            >
                              {row.skipReason === 'No term in scope'
                                ? 'No term in scope — skipped'
                                : `Skipped · ${row.skipReason}`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {renderRowRemaining(row)}
                        </TableCell>
                        <TableCell align="right">
                          {row.units ? translateQuotaUnits(row.units) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {renderRowAmount(row)}
                        </TableCell>
                      </TableRow>

                      {expandable &&
                        expanded &&
                        row.children.map(child => (
                          <TableRow
                            key={`${row.pa.id}-${child.term.id}`}
                            sx={{
                              opacity: child.skipped ? 0.5 : 1,
                              bgcolor: 'action.hover',
                            }}
                          >
                            <TableCell padding="checkbox" />
                            <TableCell padding="checkbox" />
                            <TableCell sx={{pl: 4}}>
                              <Typography variant="body2">
                                {termLabel(child)}
                              </Typography>
                              {child.skipped && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{display: 'block'}}
                                >
                                  Skipped · {child.skipReason}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {renderQuota(child.remaining, row.units)}
                            </TableCell>
                            <TableCell align="right">
                              {row.units ? translateQuotaUnits(row.units) : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {child.skipped
                                ? '—'
                                : renderQuota(child.amount, row.units)}
                            </TableCell>
                          </TableRow>
                        ))}
                    </React.Fragment>
                  );
                })}
                {plan.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No products to allocate.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {unitTotals.length > 0 && (
                  <TableRow>
                    <TableCell />
                    <TableCell />
                    <TableCell sx={{fontWeight: 600}}>Total</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" sx={{fontWeight: 600}}>
                      {unitTotals
                        .map(([units, amount]) => renderQuota(amount, units))
                        .join(' · ')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3}}
      >
        {!summary && (
          <Typography variant="caption" color="text.secondary">
            This bulk allocation cannot be undone.
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            width: '100%',
          }}
        >
          <Button onClick={handleClose} variant="outlined">
            {summary ? 'Done' : 'Cancel'}
          </Button>
          {!summary && (
            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {running
                ? 'Allocating…'
                : `Allocate to ${productCount} product${
                    productCount === 1 ? '' : 's'
                  }`}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

BulkAllocationDialog.propTypes = {
  accountId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  productAccesses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      edp_id: PropTypes.string,
      quota_total: PropTypes.number,
      quota_used: PropTypes.number,
      quota_units: PropTypes.string,
      unlimited_quota: PropTypes.bool,
      term_start_date: PropTypes.string,
      term_end_date: PropTypes.string,
    })
  ),
};

export default BulkAllocationDialog;
